import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { ItemService } from 'src/app/core/services/item.service';
import {
  HeaderModel,
  SummaryItemEnum,
  SummaryItemNameEnum,
  SummaryItemStatusEnum,
} from './header.model';
import { Company } from './../../../../core/models/company';
import { CompanyType } from 'src/app/core/infrastructure/enums/company-type.enum';
import { getMarketPlace } from '@stockaid/utils';
import { getCurrencyCodeByMarketplaceId } from 'src/app/core/constants/amazon-link.constant';
import { Currencies } from 'src/app/core/constants/currency.constant';
import { DomSanitizer } from '@angular/platform-browser';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit {
  @Input() currentCompany: Company;
  @Input() isInteraction = true;
  @Input() set dataRows(value: HeaderModel[]) {
    this._dataRows = value;
  }

  marketPlaceName: string = '';
  readonly SummaryItemNameEnum = SummaryItemNameEnum;
  readonly SummaryItemStatusEnum = SummaryItemStatusEnum;

  get dataRows(): HeaderModel[] {
    return this._dataRows;
  }

  get displayCurrency() {
    const currencyCodeByMarketPlaceId = getCurrencyCodeByMarketplaceId(this.currentCompany?.marketplaceId);
    const currencyCodeSetting = this.currentCompany?.currencyCode;
    const code = currencyCodeSetting || currencyCodeByMarketPlaceId || 'USD';
    const currency = Currencies.find(currency => currency.code === code);
    return { code:currency.code, symbol:currency.symbol };
  }

  @Output() clickRow = new EventEmitter();

  _dataRows: HeaderModel[];
  isSummary = true;

  readonly CompanyType = CompanyType;

  constructor(public itemService: ItemService, private sanitizer: DomSanitizer) {}

  ngOnInit(): void {
    this.marketPlaceName = getMarketPlace(this.currentCompany?.marketplaceId);
  }

  onClickItem(name: SummaryItemEnum): void {
    this.clickRow.emit(name);
  }

  isHiddenTextDecoration(name: string) {
    switch (name) {
      case SummaryItemNameEnum.TotalUnitsOnSuggestedPurchaseOrders:
      case SummaryItemNameEnum.TotalItemsOnSuggestedPurchaseOrders:
      case SummaryItemNameEnum.TotalCostOfSuggestedPurchaseOrders:
      case SummaryItemNameEnum.SuggestedPurchaseOrders:
        return true;
      default:
        return false;
    }
  }

  getIcon(name: string) {
    switch (name) {
      case SummaryItemNameEnum.AmazonItems:
      case SummaryItemNameEnum.ItemsOutOfStock:
      case SummaryItemNameEnum.SuggestedPurchaseOrders:
        if (
          [CompanyType.CSV, CompanyType.QBFS].includes(
            this.currentCompany?.companyType as CompanyType
          ) &&
          name !== SummaryItemNameEnum.AmazonItems
        ) {
          return 'fa fa-store';
        }
        return 'fab fa-amazon';
      case SummaryItemNameEnum.WarehouseItems:
      case SummaryItemNameEnum.TotalCostOfSuggestedPurchaseOrders:
        return 'feather icon-home';
      case SummaryItemNameEnum.NewItems:
      case SummaryItemNameEnum.TotalItemsOnSuggestedPurchaseOrders:
      case SummaryItemNameEnum.TotalEstimatedStorageFees:
        return 'feather icon-package';
      case SummaryItemNameEnum.NoSuppliersAssigned:
      case SummaryItemNameEnum.TotalUnitsOnSuggestedPurchaseOrders:
        return 'feather icon-alert-triangle';
    }
  }

  getIconNew(name: string) {
    const svgAmazoneItems = `<svg class="card-title-custom" width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M3.5 16c4 2.5 13 3 17-1" stroke="currentColor" stroke-width="2" stroke-linecap="round"></path><path d="M17 13c1 .8 2 2 2 4" stroke="currentColor" stroke-width="2" stroke-linecap="round"></path></svg>`;
    const svgWarehouseItems = `<svg class="card-title-custom" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 21V9l9-6 9 6v12"></path><path d="M9 21v-6h6v6"></path></svg>`;
    const svgNewItems = `<svg class="card-title-custom" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.7l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.7l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><path d="M3.3 7L12 12l8.7-5M12 22V12"></path></svg>`;
    const svgTotal = `<svg class="card-title-custom" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></svg>`;

    switch (name) {
      case SummaryItemNameEnum.AmazonItems:
      case SummaryItemNameEnum.ItemsOutOfStock:
      case SummaryItemNameEnum.SuggestedPurchaseOrders:
        return this.sanitizer.bypassSecurityTrustHtml(svgAmazoneItems);
      case SummaryItemNameEnum.WarehouseItems:
      case SummaryItemNameEnum.TotalCostOfSuggestedPurchaseOrders:
        return this.sanitizer.bypassSecurityTrustHtml(svgWarehouseItems);
      case SummaryItemNameEnum.NewItems:
      case SummaryItemNameEnum.TotalItemsOnSuggestedPurchaseOrders:
        return this.sanitizer.bypassSecurityTrustHtml(svgNewItems);
      case SummaryItemNameEnum.TotalEstimatedStorageFees:
        return this.sanitizer.bypassSecurityTrustHtml(svgTotal);
      case SummaryItemNameEnum.NoSuppliersAssigned:
      case SummaryItemNameEnum.TotalUnitsOnSuggestedPurchaseOrders:
        return this.sanitizer.bypassSecurityTrustHtml(svgTotal);
    }
  }
}
